/**
 * Demo V3 endpoints (VERTICAL_DEMOS_V2 WS3) — typed fetchers for the four D4
 * dashboard widgets: coverage-map, ask-chart, hq-margins, plot-recall.
 *
 * All endpoints live under /api/demo-v3/{projectId}/… and are anon-safe for
 * public demo projects. Data is committed-asset/thread_core backed and
 * deterministic, so aggressive client caching is fine.
 */

import { apiClient, getAuthToken } from "./client";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

export type DemoV3CoverageDim = "alpha" | "beta" | "elevator";

export interface DemoV3AxisSummary {
  values: number[];
  min: number;
  max: number;
}

export interface DemoV3CoverageSummary {
  project_id: string;
  dims: DemoV3CoverageDim[];
  n_samples: number;
  n_holes: number;
  axes: Record<DemoV3CoverageDim, DemoV3AxisSummary>;
  hole_predicates: string[];
}

export interface DemoV3CoveragePoint {
  alpha: number;
  beta: number;
  elevator: number;
}

export interface DemoV3CoveragePoints {
  project_id: string;
  dims: DemoV3CoverageDim[];
  elevator: number | "all";
  points: DemoV3CoveragePoint[];
}

export type DemoV3CoverageStatus = "interior" | "on_hull" | "extrapolated";

export interface DemoV3NearestSample extends DemoV3CoveragePoint {
  distance: number;
}

export interface DemoV3Classification {
  query: DemoV3CoveragePoint;
  status: DemoV3CoverageStatus;
  hull_distance: number;
  thinness: number;
  nearest: DemoV3NearestSample[];
}

export interface DemoV3SampleNextItem extends DemoV3CoveragePoint {
  rank: number;
  status: DemoV3CoverageStatus;
  thinness: number;
}

export interface DemoV3SampleNext {
  project_id: string;
  top: number;
  n_candidates: number;
  expansion_probes: DemoV3CoveragePoint[];
  items: DemoV3SampleNextItem[];
}

export interface DemoV3HqBoundaryLevels {
  L1: Record<string, number | number[]>;
  L2: Record<string, number | number[]>;
  L3?: Record<string, number | number[]>;
}

export interface DemoV3ShortPeriodAssessment {
  available: boolean;
  spec: string;
  wn_rad_s: number;
  zeta: number;
  n_alpha: number;
  CAP: number;
  level: number;
  margins_L1: Record<string, number>;
  boundaries: {
    L1: { zeta: [number, number]; CAP: [number, number] };
    L2: { zeta: [number, number]; CAP: [number, number] };
  };
}

export interface DemoV3DutchRollAssessment {
  available: boolean;
  spec: string;
  wn_rad_s: number;
  zeta: number;
  zeta_wn: number;
  level: number;
  margins_L1: Record<string, number>;
  boundaries: Record<"L1" | "L2" | "L3", { zeta_min: number; zeta_wn_min: number; wn_min: number }>;
}

export interface DemoV3HqAssessment {
  spec: string;
  short_period: DemoV3ShortPeriodAssessment;
  dutch_roll: DemoV3DutchRollAssessment;
  roll: { tau_s: number; level: number; boundaries: Record<string, { tau_max_s: number }> };
  spiral: { time_to_double_s: number; stable: boolean; level: number };
  overall_level: number;
}

export interface DemoV3Mode {
  eig_re: number;
  eig_im: number;
  wn_rad_s: number;
  zeta: number;
  period_s: number | null;
  name: string;
}

export interface DemoV3Hq {
  hq: DemoV3HqAssessment;
  modes: { lon: DemoV3Mode[]; lat: DemoV3Mode[] };
}

export interface DemoV3DrLocusPoint {
  fraction: number;
  m: number;
  Ixx: number;
  Izz: number;
  dutch_roll: { wn_rad_s: number; zeta: number; zeta_wn: number; level: number };
  roll_level: number;
  spiral_level: number;
}

export interface DemoV3DrLocus {
  spec: string;
  boom_y: number;
  crossing_fraction: number;
  points: DemoV3DrLocusPoint[];
}

export interface DemoV3AskChartOutput {
  mean: number;
  std: number;
}

export interface DemoV3AskChartResult {
  query: Record<string, number>;
  n_training: number;
  outputs: { CL: DemoV3AskChartOutput; CD: DemoV3AskChartOutput; L_D: DemoV3AskChartOutput };
  engine: string;
}

export interface DemoV3PlotRecallRanked {
  id: string;
  kind: string | null;
  distance: number;
  confidence: number;
  match_kind: string;
}

export interface DemoV3PlotRecallResult {
  match: "definitive" | "ambiguous" | "none";
  recovered_artifact_id: string | null;
  plot: string | null;
  distance: number | null;
  confidence: number | null;
  match_kind: string | null;
  resolve_url: string | null;
  ranked: DemoV3PlotRecallRanked[];
  /** Global Selvedge lookup fields. The legacy project-scoped endpoint omits them. */
  project_id?: string | null;
  project_name?: string | null;
  project_slug?: string | null;
  artifact_id?: string | null;
  plot_spec_id?: string | null;
  plot_title?: string | null;
  canonical_analysis_artifact_id?: string | null;
  manifest_sha256?: string | null;
  manifest?: Record<string, unknown> | null;
  plot_artifact_version_id?: string | null;
  verified?: boolean;
  deep_link?: string | null;
  thread_url?: string | null;
  match_reason?: string | null;
  proof?: DemoV3PlotRecallProof;
  project?: { id: string; name?: string | null; slug?: string | null } | null;
}

export type DemoV3PlotRecallProof =
  | string
  | string[]
  | Record<string, unknown>
  | Array<string | Record<string, unknown>>
  | null;

export interface DemoV3X57AskAction {
  version: 1;
  type: "open_artifact";
  project_id: string;
  plot_spec_id: string;
  artifact_id: string;
  plot_artifact_id?: string | null;
  manifest_sha256?: string | null;
  deep_link: string;
  reason: string;
  proof?: Record<string, unknown> | null;
}

export interface DemoV3X57StallAskRequest {
  question: string;
  rpm?: number;
  mass_kg?: number;
  mass_sigma_kg?: number;
  /** Canonical analysis artifact accepted by the numerical evidence service. */
  artifact_id?: string;
  manifest_sha256?: string;
  use_llm?: boolean;
  /** Recovered Thread context retained separately from the analysis artifact. */
  recovered_project_id?: string;
  recovered_artifact_id?: string;
  plot_spec_id?: string;
}

export interface DemoV3X57StallAskResult {
  answer: string;
  mode: "llm_grounded" | "deterministic" | "deterministic_fallback" | string;
  model: string | null;
  llm_available: boolean;
  llm_reason?: string | null;
  evidence: Record<string, unknown>;
  actions?: DemoV3X57AskAction[];
}

export function fetchDemoCoverageSummary(projectId: string): Promise<DemoV3CoverageSummary> {
  return apiClient<DemoV3CoverageSummary>(`/demo-v3/${projectId}/coverage/summary`);
}

export function fetchDemoCoveragePoints(projectId: string, de: number | "all" = "all"): Promise<DemoV3CoveragePoints> {
  const params = new URLSearchParams({ de: String(de) });
  return apiClient<DemoV3CoveragePoints>(`/demo-v3/${projectId}/coverage/points?${params.toString()}`);
}

export function fetchDemoCoverageClassify(
  projectId: string,
  query: { alpha: number; beta?: number; elevator?: number },
): Promise<DemoV3Classification> {
  const params = new URLSearchParams({ alpha: String(query.alpha) });
  if (query.beta !== undefined) params.set("beta", String(query.beta));
  if (query.elevator !== undefined) params.set("elevator", String(query.elevator));
  return apiClient<DemoV3Classification>(`/demo-v3/${projectId}/coverage/classify?${params.toString()}`);
}

export function fetchDemoSampleNext(projectId: string, top = 8): Promise<DemoV3SampleNext> {
  const params = new URLSearchParams({ top: String(top) });
  return apiClient<DemoV3SampleNext>(`/demo-v3/${projectId}/coverage/sample-next?${params.toString()}`);
}

export function fetchDemoHq(projectId: string): Promise<DemoV3Hq> {
  return apiClient<DemoV3Hq>(`/demo-v3/${projectId}/hq`);
}

export function fetchDemoDrLocus(projectId: string): Promise<DemoV3DrLocus> {
  return apiClient<DemoV3DrLocus>(`/demo-v3/${projectId}/dr-locus`);
}

export function fetchDemoAskChart(
  projectId: string,
  query: Record<string, number>,
): Promise<DemoV3AskChartResult> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) params.set(key, String(value));
  return apiClient<DemoV3AskChartResult>(`/demo-v3/${projectId}/ask-chart?${params.toString()}`);
}

/**
 * Multipart upload — bypasses apiClient (which JSON-encodes bodies). Same auth
 * pattern: bearer token when a session exists, cookies always. Throws
 * `Error("API <status>: …")` like apiClient so callers can branch on 404/501.
 */
export async function postDemoPlotRecall(projectId: string, file: File | Blob): Promise<DemoV3PlotRecallResult> {
  return postPlotRecallUpload(`/demo-v3/${encodeURIComponent(projectId)}/plot-recall`, file);
}

/** Search every accessible Thread project for the plot represented by an image. */
export async function postGlobalDemoPlotRecall(file: File | Blob): Promise<DemoV3PlotRecallResult> {
  return postPlotRecallUpload("/demo-v3/plot-recall", file);
}

async function postPlotRecallUpload(path: string, file: File | Blob): Promise<DemoV3PlotRecallResult> {
  const token = await getAuthToken();
  const form = new FormData();
  form.append("file", file, file instanceof File ? file.name : "screenshot.png");
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers,
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.clone().json();
      detail = typeof body?.detail === "string" ? body.detail : JSON.stringify(body?.detail ?? "");
    } catch {
      detail = await res.text().catch(() => "Unknown error");
    }
    throw new Error(`API ${res.status}: ${detail}`);
  }
  return res.json() as Promise<DemoV3PlotRecallResult>;
}

/** Evidence-first X-57 reasoning. The LLM narrates computed evidence; it does not do the math. */
export function postDemoX57StallAsk(
  projectId: string,
  body: DemoV3X57StallAskRequest,
): Promise<DemoV3X57StallAskResult> {
  return apiClient<DemoV3X57StallAskResult>(
    `/demo-v3/${encodeURIComponent(projectId)}/stall-speed/ask`,
    { method: "POST", body },
  );
}
